import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { wallboardApi } from '../../../api/client';
import TenantPicker, { useTenantFilter } from './TenantPicker';
import HelpButton from '../../help/HelpButton';

/**
 * ACD > Agent State — real-time per-agent state with a LIVE ticking
 * "time in state" clock.
 *
 * Supervisor ask (2026-08-12): previously supervisors could only see how long
 * an agent spent on break/lunch AFTER they returned to Ready. This view shows
 * — right now — how long the agent has been in their CURRENT state (e.g.
 * "Lisa · Lunch · since 12:34 PM · 00:06:12 and counting").
 *
 * How the live clock works without hammering the server: the backend returns
 * each agent's authoritative `since` timestamp + a `server_now`. We compute
 * the server↔client clock skew once per fetch and tick every second locally.
 * The snapshot itself only refetches every 5s (to catch state transitions).
 */

const REFRESH_MS = 5_000;

// "Long in state" thresholds (seconds) — past this, the timer turns red so a
// forgotten lunch / stuck wrap pops. AUX/wrap are the ones supervisors care
// about; on_call is informational (calls can legitimately run long).
const WARN_SEC = {
  aux:              10 * 60,   // 10 min break/lunch
  wrap:             60,        // 60s wrap
  ready:            null,      // no warn on ready
  on_call:          null,
  on_call_outbound: null,
  on_hold:          2 * 60,
};

const STATE_LABEL = {
  ready: 'Ready',
  aux: 'AUX',
  wrap: 'Wrap',
  on_call: 'On Call ↙',
  on_call_outbound: 'On Outbound ↗',
  on_hold: 'On Hold',
  logout: 'Not Ready',
};

const STATE_COLOR = {
  ready:            'bg-accent-green/20 text-green-700 dark:text-green-300 border-accent-green/40',
  aux:              'bg-accent-amber/20 text-amber-700 dark:text-amber-300 border-accent-amber/40',
  wrap:             'bg-accent-blue/20 text-blue-700 dark:text-blue-300 border-accent-blue/40',
  on_call:          'bg-accent-purple/20 text-purple-700 dark:text-purple-300 border-accent-purple/40',
  on_call_outbound: 'bg-teal-100 text-teal-700 dark:text-teal-300 border-teal-400/40',
  on_hold:          'bg-accent-orange/20 text-orange-700 dark:text-orange-300 border-accent-orange/40',
  logout:           'bg-surface-subtle text-surface-muted border-surface-border',
};

// Order for the count summary chips.
const COUNT_ORDER = ['ready', 'on_call', 'on_call_outbound', 'on_hold', 'wrap', 'aux'];

function pad(n) { return String(n).padStart(2, '0'); }

// hh:mm:ss (drops the hours segment under an hour).
function fmtElapsed(sec) {
  if (sec == null || sec < 0) sec = 0;
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

// Wall-clock "12:34 PM" from an ISO timestamp.
function fmtClock(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '—';
  }
}

function stateLabel(r) {
  const base = STATE_LABEL[r.state] || r.state;
  if (r.state === 'aux') {
    // Prefer the human AUX label (Lunch/Break/...) resolved from aux_codes.
    return r.aux_label || (r.aux_reason ? r.aux_reason : 'AUX');
  }
  return base;
}

export default function AgentStateView() {
  const [clientId, setClientId] = useTenantFilter();
  const { data, isLoading, error } = useQuery({
    queryKey: ['wallboard', 'agent-state', clientId || ''],
    queryFn: () => wallboardApi.agentState(clientId || undefined),
    refetchInterval: REFRESH_MS,
  });

  const [stateFilter, setStateFilter] = useState('');
  const [queueFilter, setQueueFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [search, setSearch] = useState('');

  // Local per-second tick so the elapsed clocks count up between refetches.
  const [, forceTick] = useState(0);
  const skewRef = useRef(0);  // serverNow - clientNow, ms
  useEffect(() => {
    if (data?.server_now) {
      skewRef.current = new Date(data.server_now).getTime() - Date.now();
    }
  }, [data?.server_now]);
  useEffect(() => {
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const rows = data?.rows || [];

  // Filter option lists derived from the live snapshot (match QueuesView).
  const { queueOpts, agentOpts } = useMemo(() => {
    const queues = new Set();
    const agents = new Map();
    for (const r of rows) {
      for (const qid of (r.queues || [])) queues.add(qid);
      if (r.extension) agents.set(r.extension, r.name || r.extension);
    }
    return {
      queueOpts: [...queues].sort(),
      agentOpts: [...agents.entries()].map(([ext, name]) => ({ ext, name }))
        .sort((a, b) => a.ext.localeCompare(b.ext)),
    };
  }, [rows]);

  // Live elapsed = (server-corrected now) - since. Falls back to the
  // server-computed elapsed_sec if `since` is missing.
  const nowMs = Date.now() + skewRef.current;
  const withLive = useMemo(() => {
    return rows.map((r) => {
      let live = r.elapsed_sec ?? 0;
      if (r.since) {
        const d = Math.floor((nowMs - new Date(r.since).getTime()) / 1000);
        if (d >= 0) live = d;
      }
      return { ...r, live };
    });
  }, [rows, nowMs]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return withLive
      .filter((r) => !stateFilter || r.state === stateFilter)
      .filter((r) => !queueFilter || (r.queues || []).includes(queueFilter))
      .filter((r) => !agentFilter || r.extension === agentFilter)
      .filter((r) => !q
        || (r.name || '').toLowerCase().includes(q)
        || (r.extension || '').includes(q))
      .sort((a, b) => b.live - a.live);
  }, [withLive, stateFilter, queueFilter, agentFilter, search]);

  const anyFilter = stateFilter || queueFilter || agentFilter || search;

  const counts = data?.counts || {};
  const totalLoggedIn = rows.length;

  if (error) {
    return <div className="p-6 text-sm text-accent-red">Failed to load: {error.message}</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold inline-block align-middle">Agent State — live</h2>
          <div className="ml-4 inline-block align-middle">
            <TenantPicker value={clientId} onChange={setClientId} />
          </div>
          <p className="text-xs text-surface-muted">
            Refreshes every {REFRESH_MS / 1000}s; time-in-state ticks live. Logged-out agents hidden.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <HelpButton moduleKey="acd_agent_state" />
          <select
            value={queueFilter}
            onChange={(e) => setQueueFilter(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
          >
            <option value="">All queues</option>
            {queueOpts.map((qid) => (
              <option key={qid} value={qid}>{qid}</option>
            ))}
          </select>
          <select
            value={agentFilter}
            onChange={(e) => setAgentFilter(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
          >
            <option value="">All agents</option>
            {agentOpts.map((a) => (
              <option key={a.ext} value={a.ext}>{a.ext} · {a.name}</option>
            ))}
          </select>
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin"
          >
            <option value="">All states</option>
            {COUNT_ORDER.map((s) => (
              <option key={s} value={s}>{STATE_LABEL[s]}</option>
            ))}
          </select>
          <input
            type="search"
            placeholder="Agent / ext"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-2 py-1 bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border rounded-thin w-32"
          />
          {anyFilter && (
            <button
              onClick={() => { setStateFilter(''); setQueueFilter(''); setAgentFilter(''); setSearch(''); }}
              className="px-2 py-1 rounded-thin border border-surface-border dark:border-surface-dark-border"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* State count summary chips */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <span className="px-2 py-1 rounded-full border border-surface-border dark:border-surface-dark-border">
          {queueFilter ? `In ${queueFilter}` : 'Logged in'} <strong className="ml-1">{queueFilter ? filtered.length : totalLoggedIn}</strong>
        </span>
        {COUNT_ORDER.filter((s) => counts[s]).map((s) => (
          <button
            key={s}
            onClick={() => setStateFilter((cur) => (cur === s ? '' : s))}
            className={`px-2 py-1 rounded-full border transition ${STATE_COLOR[s] || STATE_COLOR.logout} ${stateFilter === s ? 'ring-2 ring-continuum-400' : ''}`}
          >
            {STATE_LABEL[s]} <strong className="ml-1">{counts[s]}</strong>
          </button>
        ))}
      </div>

      {isLoading && !rows.length && <div className="text-sm text-surface-muted">Loading…</div>}
      {!isLoading && rows.length === 0 && (
        <div className="text-sm text-surface-muted">No agents currently logged in.</div>
      )}

      {rows.length > 0 && (
        <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel shadow-thin overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-surface-muted text-left border-b border-surface-border dark:border-surface-dark-border">
              <tr>
                <th className="px-4 py-2">Agent</th>
                <th className="px-4 py-2">State</th>
                <th className="px-4 py-2 text-right">Time in state</th>
                <th className="px-4 py-2">Since</th>
                <th className="px-4 py-2">Queue(s)</th>
                <th className="px-4 py-2 text-right">Inbound</th>
                <th className="px-4 py-2 text-right">Outbound</th>
                <th className="px-4 py-2 text-right">Total</th>
                {!clientId && <th className="px-4 py-2">Tenant</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const warn = WARN_SEC[r.state];
                const over = warn != null && r.live >= warn;
                return (
                  <tr key={r.extension} className="border-b border-surface-border/50 dark:border-surface-dark-border/50">
                    <td className="px-4 py-2">
                      <span className="font-mono text-xs text-surface-muted mr-2">{r.extension}</span>
                      <span className="font-medium">{r.name}</span>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${STATE_COLOR[r.state] || STATE_COLOR.logout}`}>
                        {stateLabel(r)}
                      </span>
                    </td>
                    <td className={`px-4 py-2 text-right font-mono tabular-nums font-semibold ${over ? 'text-accent-red' : ''}`}>
                      {fmtElapsed(r.live)}
                    </td>
                    <td className="px-4 py-2 text-surface-muted">{fmtClock(r.since)}</td>
                    <td className="px-4 py-2 text-xs text-surface-muted">
                      {r.queues && r.queues.length ? r.queues.join(', ') : '—'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.calls_today_in ?? 0}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{r.calls_today_out ?? 0}</td>
                    <td className="px-4 py-2 text-right tabular-nums font-semibold">{r.calls_today ?? 0}</td>
                    {!clientId && <td className="px-4 py-2 text-xs text-surface-muted">{r.client_name || '—'}</td>}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={clientId ? 8 : 9} className="px-4 py-6 text-center text-sm text-surface-muted">
                    No agents match the current filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
